import React from 'react';
import { Box, Typography, Paper, Chip, Grid, Divider, Button } from '@mui/material';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowBack as BackIcon,
  CalendarToday as DateIcon,
  Person as AuthorIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { BlogLayout, BlogCard, BlogTags, MarkdownRenderer } from '../../components/blog';
import { getPostBySlug, getRelatedPosts } from '../../data/blog.generated';
import { getCategoryLabel, getCategoryColor } from '../../types/blog';
import { useDocumentTitle } from '../../hooks/core';

export const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const post = slug ? getPostBySlug(slug) : undefined;

  useDocumentTitle(post ? `${post.title} - Blog` : 'Blog');

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const relatedPosts = getRelatedPosts(post, 3);
  const categoryColor = getCategoryColor(post.category);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <BlogLayout
      title=""
      showSearch={false}
      breadcrumbs={[
        { label: getCategoryLabel(post.category), path: `/blog/category/${post.category}` },
        { label: post.title },
      ]}
    >
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{post.title} | SquareUp Blog</title>
        <meta name="description" content={post.description} />
        {/* Open Graph */}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:url" content={`https://squareitup.app/blog/${post.slug}`} />
        <meta property="og:site_name" content="SquareUp" />
        {post.featuredImage && <meta property="og:image" content={post.featuredImage} />}
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author} />
        {post.tags.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.description} />
        {post.featuredImage && <meta name="twitter:image" content={post.featuredImage} />}
        <link rel="canonical" href={`https://squareitup.app/blog/${post.slug}`} />
      </Helmet>

      {/* Back button */}
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate('/blog')}
        sx={{ mb: 3 }}
      >
        Back to Blog
      </Button>

      {/* Article */}
      <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', overflow: 'hidden' }}>
        {/* Featured image */}
        {post.featuredImage && (
          <Box
            sx={{
              width: '100%',
              height: { xs: 200, md: 400 },
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={post.featuredImage}
              alt={post.title}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
        )}

        {/* Content */}
        <Box sx={{ p: { xs: 3, md: 5 } }}>
          {/* Category */}
          <Box sx={{ mb: 2 }}>
            <Chip
              label={getCategoryLabel(post.category)}
              size="small"
              onClick={() => navigate(`/blog/category/${post.category}`)}
              sx={{
                bgcolor: `${categoryColor}15`,
                color: categoryColor,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            />
          </Box>

          {/* Title */}
          <Typography variant="h3" component="h1" fontWeight="bold" sx={{ mb: 2 }}>
            {post.title}
          </Typography>

          {/* Description */}
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            {post.description}
          </Typography>

          {/* Meta info */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 3,
              mb: 4,
              color: 'text.secondary',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AuthorIcon fontSize="small" />
              <Typography variant="body2">{post.author}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DateIcon fontSize="small" />
              <Typography variant="body2">{formatDate(post.date)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TimeIcon fontSize="small" />
              <Typography variant="body2">{post.readingTime} min read</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Article content */}
          <MarkdownRenderer content={post.content} />

          {/* Tags */}
          {post.tags.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle2" gutterBottom>
                Tags
              </Typography>
              <BlogTags tags={post.tags} />
            </Box>
          )}
        </Box>
      </Paper>

      {/* Related posts */}
      {relatedPosts.length > 0 && (
        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Related Posts
          </Typography>
          <Grid container spacing={3}>
            {relatedPosts.map((relatedPost) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={relatedPost.slug}>
                <BlogCard post={relatedPost} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </BlogLayout>
  );
};

export default BlogPostPage;
